import { useEffect, useRef, useState } from 'react';

import { extractSmartLinkInlineIcon } from '@atlaskit/link-extractors/extract-smart-link-inline-icon';
import { extractSmartLinkTitle } from '@atlaskit/link-extractors/extract-smart-link-title';
import CardClient from '@atlaskit/link-provider/client';

import type { SessionContextItem } from './SessionContext';

const CARD_CLIENT = new CardClient();

type ContextMetadata = {
	url: string;
	title?: string;
	icon?: ReturnType<typeof extractSmartLinkInlineIcon>;
};

/** Shares primary/popup metadata and retains attempted resolutions across disclosure opens. */
export const useSessionContextMetadata = (
	contexts: readonly (SessionContextItem & { url: string })[],
	isOpen: boolean,
): ReadonlyMap<string, ContextMetadata> => {
	const requests = useRef(new Map<string, Promise<ContextMetadata | undefined>>());
	const [metadata, setMetadata] = useState<ReadonlyMap<string, ContextMetadata>>(new Map());
	// Stable dependencies even when a caller recreates its presentation array on every render.
	const urlsKey = JSON.stringify(
		(isOpen ? contexts : contexts.slice(0, 1)).map((context) => context.url.trim()),
	);

	useEffect(() => {
		let cancelled = false;
		const urls: string[] = JSON.parse(urlsKey);
		for (const url of new Set(urls)) {
			// Relative file/image URLs belong to the caller's preview, not ORS.
			if (url.startsWith('/')) {
				continue;
			}
			let request = requests.current.get(url);
			if (!request) {
				// Keep the existing appearance for consistent extraction and client cache reuse.
				// Queue together so CardClient can batch; never force a refresh on popup opening.
				request = CARD_CLIENT.fetchData(url, false, 'embed')
					.then((response) => ({
						url,
						title: extractSmartLinkTitle(response),
						icon: extractSmartLinkInlineIcon(response, false),
					}))
					.catch(() => undefined);
				requests.current.set(url, request);
			}
			void request.then((resolved) => {
				if (!cancelled && resolved) {
					setMetadata((previous) =>
						previous.get(url) === resolved ? previous : new Map(previous).set(url, resolved),
					);
				}
			});
		}
		return () => {
			cancelled = true;
		};
	}, [urlsKey]);

	return metadata;
};
